<template>
  <div class="shopping-list">
    <ShoppingListToolbar
      :can-filter="hasPerm('accounts.shopping_list_filter_by_day')"
      :can-sort="hasPerm('accounts.shopping_list_sort')"
      :count="ingredientsCount"
      :group-by="groupBy"
      :items="listForItems"
      :list-for="listFor"
      @group-by="groupBy = $event"
      @list-for="setListFor"
      @upgrade="globalStore.showAppUpgradePlanModal"
    />
    <ShoppingListHeader
      :icon="getIconBySort"
      :sort-by="sortBy"
      @sort="hasPerm('accounts.shopping_list_sort') ? setSortBy($event) : globalStore.showAppUpgradePlanModal()"
    />
    <div class="list">
      <template v-if="shoppingList.ingredients.length">
        <template v-if="groupBy">
          <ShoppingListGroup
            v-for="group in shoppingList.groups"
            :key="group"
            :expanded="!closedGroups.includes(group)"
            :name="group"
            @toggle="toggleGroup(group)"
          >
            <template v-for="(ingredient, index) in shoppingList.ingredients" :key="ingredient.id">
              <ShoppingListIngredientRow
                v-if="ingredient.group === group"
                :allergens-enabled="hasPerm('accounts.patient_allergens')"
                :day-short="dayShort"
                :editable="canDietBeEdited"
                :expanded="expandedIngredient === ingredient.id"
                :filters="dishFlags"
                :ingredient="ingredient"
                :replacing="activeAddDishOrIngredient === index"
                :sources="getDishesAndIngredients(ingredient, true)"
                @day="handleDayClick"
                @replace="replaceIngredient"
                @replace-end="setActiveAddDishOrIngredient()"
                @replace-start="handleChangeIngredientClick(index)"
                @toggle="toggleIngredient(ingredient.id)"
              />
            </template>
          </ShoppingListGroup>
        </template>
        <template v-else>
          <ShoppingListIngredientRow
            v-for="(ingredient, index) in shoppingList.ingredients"
            :key="ingredient.id"
            :allergens-enabled="hasPerm('accounts.patient_allergens')"
            :day-short="dayShort"
            :editable="canDietBeEdited"
            :expanded="expandedIngredient === ingredient.id"
            :filters="dishFlags"
            :ingredient="ingredient"
            :replacing="activeAddDishOrIngredient === index"
            :sources="getDishesAndIngredients(ingredient, false)"
            @day="handleDayClick"
            @replace="replaceIngredient"
            @replace-end="setActiveAddDishOrIngredient()"
            @replace-start="handleChangeIngredientClick(index)"
            @toggle="toggleIngredient(ingredient.id)"
          />
        </template>
      </template>
      <div v-else class="list-empty">Brak produktów na liście zakupów.</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { useEventListener } from '@vueuse/core'
  import { storeToRefs } from 'pinia'
  import { computed, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  import { getScheduleDayHeader } from '@/const'
  import { useDietsStore } from '@/store/dietsStore'
  import { useGlobalStore } from '@/store/globalStore'
  import { dayMapShort, getIconBySortFn } from '@/utils/common'
  import { getDietDaysCount, getDietWeeksCount } from '@/utils/dietDays'
  import { pushDataLayer } from '@/utils/pushDataLayer'
  import { reportError } from '@/utils/reportError'

  import type { Coordinates } from '@/types/Diet'

  import { buildShoppingList, sortShoppingListSources } from './buildShoppingList'
  import ShoppingListGroup from './components/ShoppingListGroup/ShoppingListGroup.vue'
  import ShoppingListHeader from './components/ShoppingListHeader/ShoppingListHeader.vue'
  import ShoppingListIngredientRow from './components/ShoppingListIngredientRow/ShoppingListIngredientRow.vue'
  import ShoppingListToolbar from './components/ShoppingListToolbar/ShoppingListToolbar.vue'
  import { useShoppingList } from './composables/useShoppingList'
  import type { ShoppingList } from './types'

  const listFor = ref('all')
  const groupBy = ref(true)
  const sortBy = ref('name')
  const sortByReverse = ref(false)
  const {
    activeReplacementIndex: activeAddDishOrIngredient,
    closedGroups,
    expandedIngredientId: expandedIngredient,
  } = useShoppingList()

  const route = useRoute()
  const router = useRouter()
  const globalStore = useGlobalStore()
  const dietsStore = useDietsStore()
  const { hasPerm } = globalStore
  const { diet, dietAllergens, canDietBeEdited } = storeToRefs(dietsStore)

  const listForItems = computed(() => [
    { id: 'all', label: 'Cały jadłospis' },
    ...Array.from({ length: getDietWeeksCount(getDietDaysCount(diet.value)) }, (_, index) => ({
      id: String(index + 1),
      label: `Tydzień ${index + 1}`,
    })),
  ])
  const dishFlags = computed(() => {
    return diet.value?.data.dish_search_filters
  })
  const shoppingList = computed(() =>
    buildShoppingList({
      allergens: dietAllergens.value,
      diet: diet.value,
      getRelatedCoordinates: dietsStore.getRelatedCoordinates,
      listFor: listFor.value,
      reverse: sortByReverse.value,
      sortBy: sortBy.value,
    }),
  )
  const ingredientsCount = computed(() => {
    const count = shoppingList.value.ingredients.length
    if (count === 1) return `${count} Produkt`
    if (count > 1 && count < 4) return `${count} Produty`
    return `${count} Produktów`
  })

  const handleChangeIngredientClick = (ingredientIndex: number) => {
    if (!canDietBeEdited.value) {
      return
    } else if (hasPerm('accounts.shopping_list_ingredient_change')) {
      setActiveAddDishOrIngredient(ingredientIndex)
    } else {
      globalStore.showAppUpgradePlanModal()
    }
  }
  const dayShort = (dayIndex: number) => {
    const assignedDate = getScheduleDayHeader(dayIndex, diet.value?.data.start_date).shortName
    if (assignedDate) return assignedDate

    return diet.value?.data.diet_type === 'days_of_week'
      ? (dayMapShort[dayIndex % dayMapShort.length] ?? `Dzień ${dayIndex + 1}`)
      : `Dzień ${dayIndex + 1}`
  }
  const getDishesAndIngredients = (
    ingredient: ShoppingList['ingredients'][number],
    sortByName: boolean,
  ) => {
    return sortShoppingListSources(
      shoppingList.value.dishesAndIngredients[ingredient.id] ?? [],
      sortByName,
    )
  }
  const toggleGroup = (groupName: string) => {
    const groupIndex = closedGroups.value.indexOf(groupName)
    if (groupIndex === -1) {
      closedGroups.value.push(groupName)
    } else {
      closedGroups.value.splice(groupIndex, 1)
    }
  }
  const toggleIngredient = (ingredientId: number) => {
    if (expandedIngredient.value === ingredientId) {
      expandedIngredient.value = undefined
    } else {
      pushDataLayer({
        event: 'diet_shoppinglist_days_expand',
      })
      expandedIngredient.value = ingredientId
    }
  }
  const setListFor = (value: number | string | null) => {
    if (value !== null) listFor.value = String(value)
  }
  const redirectToDietDetailDay = (day: Coordinates | null) => {
    if (day) {
      sessionStorage.setItem('redirectMealIndex', day.mealIndex.toString())
      sessionStorage.setItem('redirectDishOrIngredientIndex', day.dishOrIngredientIndex.toString())
      void router.push({
        name: 'DietDetailDay',
        params: {
          dayIndex: day.dayIndex,
          dietId: route.params.dietId,
          dietType: route.params.dietType,
        },
      })
    }
  }
  const handleDayClick = (day: Coordinates) => {
    if (hasPerm('accounts.shopping_list_sort')) redirectToDietDetailDay(day)
    else globalStore.showAppUpgradePlanModal()
  }
  const setSortBy = (by: string) => {
    if (sortBy.value === by) {
      sortByReverse.value = !sortByReverse.value
    } else {
      sortBy.value = by
      sortByReverse.value = false
    }
  }
  const setActiveAddDishOrIngredient = (active?: number) => {
    activeAddDishOrIngredient.value = active
  }
  const replaceIngredient = async (oldIngredientId: number, newIngredientId: number) => {
    try {
      await dietsStore.fetchAndReplaceIngredient({ newIngredientId, oldIngredientId })
      setActiveAddDishOrIngredient()
    } catch (err) {
      reportError(err, 'Error during replacing ingredient', { newIngredientId, oldIngredientId })
    }
  }
  const hideMenu = () => {
    activeAddDishOrIngredient.value = undefined
  }
  const getIconBySort = computed(() => getIconBySortFn(sortBy.value, sortByReverse.value))

  useEventListener(document, 'click', hideMenu)
</script>

<style lang="scss">
  .shopping-list {
  padding: 12px;
  margin-bottom: 24px;
  background: #fff;

  .allergens {
    display: block;
    flex: 0 0 8px;
    width: 8px;
    height: 8px;
    margin: 0 3px;
  }

  .allergens.not-like {
    background: var(--color-warning-60);
  }

  .allergens.like {
    background: var(--color-success-60);
  }

  .allergens.allergies {
    background: #faa0a8;
  }

  .allergens.intolerances {
    background: #a0a8fa;
  }

  .header {
    display: grid;
    grid-template-columns: 1fr 125px 125px 250px;
    gap: 12px;
    margin-bottom: 12px;
    font-family: Montserrat-Medium;
    font-size: 12px;
    border-bottom: 2px solid #eaeaea;
  }

  .header-name {
    display: flex;
    align-items: center;
    padding: 10px 30px;
    cursor: pointer;
  }

  .header-quantity {
    display: flex;
    align-items: center;
    justify-content: right;
    padding: 10px 0;
    cursor: pointer;
  }

  .header-measurement {
    display: flex;
    align-items: center;
    padding: 10px 0;
  }

  .sort {
    color: var(--color-primary-text);
    cursor: pointer;
  }

  .sorted .sort {
    color: var(--color-main-100);
  }

  .header-name.sorted {
    color: var(--color-main-100);
  }

  .header-quantity.sorted {
    color: var(--color-main-100);
  }

  .ingredient-name-button {
    display: inline-block;
    max-width: 100%;
    margin-right: 6px;
    overflow-wrap: break-word;
    cursor: pointer;
  }

  .ingredient-name-button:hover {
    font-family: Montserrat-SemiBold;
  }

  .ingredient-quantity {
    display: flex;
    align-items: center;
    justify-content: right;
    margin-right: 16px;
  }

  .ingredient-measurement {
    display: flex;
    align-items: center;
    justify-content: left;
  }

  .ing-container {
    display: contents;
  }

  .ing-container:hover .ingredient-name-button,
  .ing-container:hover .ingredient-quantity,
  .ing-container:hover .ingredient-measurement,
  .ing-container.active .ingredient-name-button,
  .ing-container.active .ingredient-quantity,
  .ing-container.active .ingredient-measurement {
    font-family: Montserrat-SemiBold;
  }

  .expander {
    cursor: pointer;
  }

  .list {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 125px) minmax(0, 125px) minmax(0, 250px);
    gap: 12px;
    font-size: 12px;
  }

  .list-empty {
    grid-column: span 4;
    padding: 0 30px;
  }

  .group {
    display: flex;
    grid-column: 1 / span 4;
    align-items: center;
    height: 32px;
    cursor: pointer;
    background: #f5f5f5;
  }

  .group-name {
    font-family: Montserrat-Medium;
  }

  .group .expander {
    margin: auto 6px;
  }

  .ingredient-name {
    padding: 0 30px;
  }

  .ingredient-name-with-allergens {
    display: flex;
    align-items: center;
  }

  .ingredient-name-button.expanded {
    font-family: Montserrat-SemiBold;
  }

  .ingredient-actions {
    display: flex;
    align-items: center;
    justify-content: right;
  }

  .ingredient-actions .expander {
    color: var(--color-primary-text);
  }

  .dishes-and-ingredients {
    display: grid;
    grid-template-columns: 1fr min-content;
    grid-column: span 4;
    row-gap: 12px;
    padding: 6px 6px 6px 36px;
    background: var(--color-main-10);
    border-radius: 4px;
  }

  .dish-or-ingredient-name {
    display: flex;
    align-items: center;
  }

  .dish-or-ingredient-days {
    display: grid;
    grid-template-columns: repeat(7, max-content);
    gap: 4px;
    align-items: center;
    justify-content: flex-end;
  }

  .dish-or-ingredient-day {
    height: 24px;
    padding: 0 6px;
    margin-left: 2px;
    font-family: Montserrat-Medium;
    line-height: 24px;
    text-align: center;
    background: #fff;
    border-radius: 4px;
  }

  .dish-or-ingredient-day.active {
    font-family: Montserrat-SemiBold;
    color: #fff;
    cursor: pointer;
    background: var(--color-main-100);
  }

  .dish-or-ingredient-day.active:hover {
    background: var(--color-main-110);
  }

  .ingredient-name-button.disabled {
    cursor: default;
  }

  .add-ingredient-input {
    width: 350px;
  }
  }
</style>
